import { useEffect, useRef } from "react";

export function useFocusTrap(isActive: boolean, onClose: () => void) {
  const focusableElementsRef = useRef<HTMLElement[]>([]);
  const firstFocusableElementRef = useRef<HTMLElement | null>(null);
  const lastFocusableElementRef = useRef<HTMLElement | null>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isActive && containerRef.current) {
      previouslyFocusedElementRef.current =
        document.activeElement as HTMLElement;

      const focusableElements = Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(
          'a, button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])',
        ),
      );

      if (focusableElements.length > 0) {
        focusableElementsRef.current = focusableElements;
        firstFocusableElementRef.current = focusableElements[0];
        lastFocusableElementRef.current =
          focusableElements[focusableElements.length - 1];

        firstFocusableElementRef.current.focus();
      }

      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.removeEventListener("keydown", handleKeyDown);

      previouslyFocusedElementRef.current?.focus();
    }

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isActive]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
    }

    if (e.key === "Tab") {
      if (focusableElementsRef.current.length === 0) return;

      if (
        e.shiftKey &&
        document.activeElement === firstFocusableElementRef.current
      ) {
        e.preventDefault();
        lastFocusableElementRef.current?.focus();
      } else if (
        !e.shiftKey &&
        document.activeElement === lastFocusableElementRef.current
      ) {
        e.preventDefault();
        firstFocusableElementRef.current?.focus();
      }
    }
  };

  return containerRef;
}
